<script setup>
import { useI18n } from 'vue-i18n';
import { computed, watch, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store.js';
import ComboBox from 'next/combobox/ComboBox.vue';
import BotTemplateFormField from './BotTemplateFormField.vue';

const props = defineProps({
  modelValue: {
    type: [Object, null],
    default: () => null,
  },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const templateId = ref(props.modelValue?.id || null);
const templateParameters = ref(props.modelValue?.parameters || {});

const store = useStore();
const templates = useMapGetter('wgptBots/getBotTemplates');
const uiFlags = useMapGetter('wgptBots/getUIFlags');
const isLoadingTemplates = computed(() => uiFlags.value.isFetchingTemplates);

const mapToOptions = (items, valueKey, labelKey) => {
  return items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey] || item[valueKey],
  }));
};

const templateList = computed(() => [
  { value: null, label: 'No template' },
  ...mapToOptions(templates.value, 'id', 'name'),
]);

const selectedTemplate = computed(() => {
  return templates.value.find(template => template.id === templateId.value);
});

const updateValue = () => {
  const value = templateId.value
    ? {
        id: templateId.value,
        parameters: templateParameters.value,
      }
    : null;
  emit('update:modelValue', value);
};

watch(templateId, () => {
  updateValue();
});

watch(
  templateParameters,
  () => {
    updateValue();
  },
  { deep: true }
);

onMounted(() => {
  store.dispatch('wgptBots/getTemplates');
});
</script>

<template>
  <div>
    <label class="block mb-4">
      {{ t('WGPT_BOTS.NEW_FLOW.CREATE.FORM.TEMPLATE.ID.LABEL') }}
      <ComboBox
        v-model="templateId"
        :options="templateList"
        :disabled="isLoadingTemplates"
        label="Template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </label>

    <div
      v-if="selectedTemplate && selectedTemplate.parameters"
      class="py-8 mt-8 space-y-4 border-t border-n-weak/50"
    >
      <h4 class="font-medium text-gray-700 dark:text-gray-300">
        {{ t('WGPT_BOTS.NEW_FLOW.CREATE.FORM.TEMPLATE.PARAMETERS.HEADER') }}
      </h4>

      <div
        v-for="parameter in selectedTemplate.parameters"
        :key="parameter.key"
        class="w-full"
      >
        <BotTemplateFormField
          v-model="templateParameters[parameter.key]"
          :parameter="parameter"
        />
      </div>
    </div>
  </div>
</template>
